import React, {forwardRef} from 'react';
import PropTypes from 'prop-types';

/**
 * A component that represents a button.
 * @param {Object} props - The props.
 * @param {string} props.alignment - The alignment.
 * @param {string} props.appearance - The appearance.
 * @param {boolean} props.disabled - Whether the button is disabled.
 * @param {string} props.href - The href.
 * @param {string} props.iconEnd - The end icon.
 * @param {string} props.iconFlipRtl - The icon flip RTL.
 * @param {string} props.iconStart - The start icon.
 * @param {string} props.kind - The kind.
 * @param {string} props.label - The label.
 * @param {boolean} props.loading - Whether the button is loading.
 * @param {Object} props.messageOverrides - The message overrides.
 * @param {string} props.name - The name.
 * @param {string} props.rel - The rel.
 * @param {boolean} props.round - Whether the button is round.
 * @param {string} props.scale - The scale.
 * @param {boolean} props.splitChild - Whether the button is a split child.
 * @param {string} props.target - The target.
 * @param {string} props.type - The type.
 * @param {string} props.width - The width.
 * @param {React.ReactNode} props.children - The children.
 * @returns {JSX.Element} The component.
 * @example
 * <CalciteButton
 * alignment="center"
 * appearance="outline"
 * disabled={false}
 * href="https://example.com"
 * iconEnd="circle"
 * iconFlipRtl="both"
 * iconStart="circle"
 * kind="brand"
 * label="Label"
 * loading={false}
 * messageOverrides="Message overrides"
 * name="Name"
 * rel="noopener"
 * round={false}
 * scale="m"
 * splitChild={false}
 * target="_blank"
 * type="button"
 * width="auto"
 * >
 *  Children
 * </CalciteButton>
 */
const CalciteButton = forwardRef(
  (
    {
      alignment,
      appearance,
      disabled,
      href,
      iconEnd,
      iconFlipRtl,
      iconStart,
      kind,
      label,
      loading,
      messageOverrides,
      name,
      rel,
      round,
      scale,
      splitChild,
      target,
      type,
      width,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <calcite-button
        ref={ref}
        class={className || undefined}
        alignment={alignment || undefined}
        appearance={appearance || undefined}
        disabled={disabled == null ? undefined : disabled}
        href={href || undefined}
        icon-end={iconEnd || undefined}
        icon-flip-rtl={iconFlipRtl || undefined}
        icon-start={iconStart || undefined}
        kind={kind || undefined}
        label={label || undefined}
        loading={loading == null ? undefined : loading}
        message-overrides={messageOverrides || undefined}
        name={name || undefined}
        rel={rel || undefined}
        round={round == null ? undefined : round}
        scale={scale || undefined}
        split-child={splitChild == null ? undefined : splitChild}
        target={target || undefined}
        type={type || undefined}
        width={width || undefined}
        {...rest}
      >
        {children}
      </calcite-button>
    );
  },
);

CalciteButton.propTypes = {
  className: PropTypes.string,
  alignment: PropTypes.oneOf([
    'center',
    'end',
    'icon-end-space-between',
    'icon-start-space-between',
    'space-between',
    'start',
  ]),
  appearance: PropTypes.oneOf(['outline', 'outline-fill', 'solid', 'transparent']),
  disabled: PropTypes.bool,
  href: PropTypes.string,
  iconEnd: PropTypes.string,
  iconFlipRtl: PropTypes.oneOf(['both', 'end', 'start']),
  iconStart: PropTypes.string,
  kind: PropTypes.oneOf(['brand', 'danger', 'inverse', 'neutral']),
  label: PropTypes.string,
  loading: PropTypes.bool,
  messageOverrides: PropTypes.object,
  name: PropTypes.string,
  rel: PropTypes.string,
  round: PropTypes.bool,
  scale: PropTypes.oneOf(['s', 'm', 'l']),
  splitChild: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['primary', 'secondary'])]),
  target: PropTypes.string,
  type: PropTypes.string,
  width: PropTypes.oneOf(['auto', 'full', 'half']),
  children: PropTypes.node,
};

export default CalciteButton;
